import { useState } from 'react'
import { Button, Spinner, useToaster } from '@enterprise-ui/canvas-ui-react'
import EnterpriseIcon, { CloudUploadIcon } from '@enterprise-ui/icons'
import { uploadTemplate } from '../../services/requests'
import {
  TOASTER_DEFAULTS,
  INVALID_TEMPLATE,
  spreadsheetFileTypeIdentifier,
} from '../../globalConfig/constants'

export const TemplateUploadBox = ({ successfulUploadCallback, vendorId }) => {
  const [dragActive, setDragActive] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [waitingOnResponse, setWaitingOnResponse] = useState(false)
  const makeToast = useToaster()

  const fileChangeHandler = (event) => {
    setSelectedFile(event?.target?.files[0])
  }

  const fileDragHandler = (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (event.type === 'dragenter' || event.type === 'dragover') {
      setDragActive(true)
    } else if (event.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const fileDropHandler = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setDragActive(false)

    if (
      event?.dataTransfer?.files &&
      event?.dataTransfer?.files[0] &&
      event?.dataTransfer?.files[0]?.type === spreadsheetFileTypeIdentifier
    ) {
      setSelectedFile(event.dataTransfer.files[0])
    } else {
      makeToast({
        ...TOASTER_DEFAULTS,
        type: 'error',
        heading: 'Error',
        message: 'Ensure your file is of type .xlsx',
      })
    }
  }

  const sendFailureToast = (responseBody) => {
    makeToast({
      ...TOASTER_DEFAULTS,
      type: 'error',
      heading: 'Error',
      message: 'Failed to process the submitted spreadsheet.',
    })
  }

  const sendInvalidTemplateFailureToast = (responseBody) => {
    makeToast({
      ...TOASTER_DEFAULTS,
      type: 'error',
      heading: 'Error',
      message:
        'We currently do not support the format of your spreadsheet. Make sure you are using the downloaded template and have not modified the column titles or order.',
    })
  }

  const sendSpreadsheetUploadRequest = (file) => {
    setWaitingOnResponse(true)
    const formData = new FormData()
    formData.append('file', file)
    uploadTemplate(formData, vendorId)
      .then((response) => {
        setWaitingOnResponse(false)
        setSelectedFile(null)
        if (response?.status === 200) {
          makeToast({
            ...TOASTER_DEFAULTS,
            type: 'success',
            heading: 'Upload Successful',
            message:
              "We're processing your spreadsheet. You can monitor its progress in the Spreadsheet History table below",
          })
          if (successfulUploadCallback) {
            successfulUploadCallback()
          }
        } else if (response?.data?.errorCode === INVALID_TEMPLATE) {
          sendInvalidTemplateFailureToast()
        } else {
          sendFailureToast()
        }
      })
      .catch((e) => {
        setWaitingOnResponse(false)
        setSelectedFile(null)
        if (e?.response?.data?.errorCode === INVALID_TEMPLATE) {
          sendInvalidTemplateFailureToast()
        } else {
          sendFailureToast()
        }
      })
  }

  if (selectedFile) {
    return (
      <div
        data-testid="templateUploadBox"
        className="hesp-upload-div hesp-position-relative"
        style={{
          backgroundColor: dragActive
            ? 'var(--canvas-background-color--placeholder)'
            : 'var(--canvas-background-color--default-contrast-weak)',
          height: '143px',
        }}
      >
        {waitingOnResponse ? (
          <>
            <span
              data-testid="upload-lockout"
              className="hesp-lockout-container hesp-lockout-overlay"
            ></span>
            <span className="hesp-lockout-container">
              <Spinner size="expanded" />
            </span>
          </>
        ) : null}
        <div className="hesp-general-marginAuto hesp-general-fullwidth">
          <div className="hesp-general-centeredText hc-mv-normal">
            <strong>{'Selected File: '}</strong>
            {selectedFile?.name}
          </div>
          <div className="hesp-general-justifyCenter hesp-general-flex hc-mv-normal">
            <Button
              className="hesp-general-fullwidth"
              style={{
                width: '10%',
                minWidth: '85px',
                marginRight: 'var(--canvas-spacing--normal)',
              }}
              type="secondary"
              onClick={() => setSelectedFile(null)}
            >
              CANCEL
            </Button>
            <Button
              data-testid="templateUploadButton"
              className="hesp-general-fullwidth"
              style={{ width: '10%', minWidth: '85px' }}
              type="primary"
              onClick={() => {
                sendSpreadsheetUploadRequest(selectedFile)
              }}
            >
              UPLOAD
            </Button>
          </div>
        </div>
      </div>
    )
  } else {
    return (
      <>
        <input
          className="hesp-general-hidden"
          id="file-upload"
          type="file"
          accept=".xlsx"
          onChange={fileChangeHandler}
        ></input>
        <label
          data-testid="upload-label"
          htmlFor="file-upload"
          onDragEnter={fileDragHandler}
          onDragLeave={fileDragHandler}
          onDragOver={fileDragHandler}
          onDrop={fileDropHandler}
        >
          <div
            data-testid="templateUploadBox"
            className="hesp-upload-div"
            style={{
              backgroundColor: dragActive
                ? 'var(--canvas-background-color--placeholder)'
                : 'var(--canvas-background-color--default-contrast-weak)',
              cursor: 'pointer',
            }}
          >
            <span
              className="hc-mv-min"
              style={{
                height: '40px',
                width: '50px',
                fontSize: '40px',
                position: 'relative',
              }}
            >
              <EnterpriseIcon
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  filter: 'brightness(175%)',
                }}
                icon={CloudUploadIcon}
                size="inline"
              />
            </span>
            <span className="hc-fs-md hc-mv-min hesp-general-centeredText hesp-general-underline">
              <strong>Click or Drag and Drop File Here to Import</strong>
            </span>
            <span className="hc-fs-sm hc-mv-min hesp-general-centeredText">
              Must be .xlsx file matching the original download formatting
            </span>
          </div>
        </label>
      </>
    )
  }
}
